import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, type ComboboxItem } from './Combobox';
import { CURRENCIES } from '@/lib/currencies';

interface Props {
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Searchable popover currency picker. Replaces both
 *   `<input type="text" placeholder="JPY">`
 * and
 *   `<select><option>USD</option>…</select>`
 * — same UX as LanguageSwitcher, scaled to 30+ currencies.
 */
export function CurrencyPicker({ value, onChange, disabled, className }: Props) {
  const { t } = useTranslation();
  const items: ComboboxItem<string>[] = useMemo(
    () => CURRENCIES.map((c) => ({
      value: c.code,
      label: `${c.code} · ${c.name}`,
      icon: c.flag,
      badge: c.symbol,
      searchAliases: [c.symbol, c.name],
    })),
    [],
  );
  return (
    <Combobox
      value={value.toUpperCase()}
      onChange={(v) => onChange(v.toUpperCase())}
      items={items}
      disabled={disabled}
      className={className}
      placeholder={t('common.selectCurrency', { defaultValue: 'Select currency…' })}
      searchPlaceholder={t('common.searchCurrency', { defaultValue: 'Search currency…' })}
    />
  );
}
